import React, { useState, memo, useEffect } from 'react';

// Material UI
import {
    styled,
    Box,
    Paper,
    CircularProgress,
    TextField,
    IconButton,
    Typography,
    Tabs,
    Tab,
    useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';

// Components
import { ControlBar, ControlBarHeader, ControlBarButton } from 'components/ui/layout';
import OrderDetailModal from 'components/orders/OrderDetailModal';
import FullScreenModal from 'components/popups/FullScreenModal';
import Comp from 'components/comp/Comp';
import { PaymentIcon, DiscountIcon, CommentIcon } from 'components/ui/icons';

// Utility
import { useAuth } from 'context/auth';
import useExpresso from 'hooks/useExpresso';
import { formatPrice } from 'utility/numbers';
import moment from 'moment-timezone';
import ReactGA from 'react-ga';

function OrderPage(props) {
    const theme = useTheme();
    let maxWidth = theme.breakpoints.values.lg;

    return (
        <Box display={'flex'} flexDirection={'column'} height="100%">
            <Box px={[2, 4]} pt={[2, 4]} pb={6} flexGrow={1}>
                <Box
                    display="flex"
                    flexDirection="column"
                    ml="auto"
                    mr="auto"
                    maxWidth={maxWidth}
                    height="100%"
                    width="100%"
                >
                    {props.children}
                </Box>
            </Box>
        </Box>
    );
}

export default function Orders(props) {
    const eventId = props.match.params.id;
    const search = props.location.search;

    // State
    const [state, setState] = useState({
        selectedOrder: null,
        refreshToken: 0,
        createOrderOpen: false,
    });

    // Utility
    const { auth } = useAuth();

    const hostId = auth.hostId;

    // If url includes the search param: o, automatically open the order with the provided id
    useEffect(() => {
        if (search.startsWith('?o=')) {
            const orderId = search.slice(3);
            setState((s) => ({ ...s, selectedOrder: orderId }));
        }
    }, [search]);

    // Set selected order to make OrderDetailModal modal appear
    const setSelectedOrder = (order) => {
        ReactGA.event({ category: 'Orders', action: 'View Order', label: `${hostId}` });
        setState((s) => ({ ...s, selectedOrder: order }));
    };

    const handleOrderUpdate = () => {
        ReactGA.event({ category: 'Orders', action: 'Update Order', label: `${hostId}` });
        setState((s) => ({ ...s, refreshToken: s.refreshToken + 1 }));
    };

    const handleOrderCreate = () => {
        ReactGA.event({ category: 'Orders', action: 'Create Order', label: `${hostId}` });
        setState((s) => ({ ...s, selectedOrder: null, refreshToken: state.refreshToken + 1 }));
    };

    return (
        <OrderPage>
            <ControlBar>
                <ControlBarHeader>Online Orders</ControlBarHeader>
                <ControlBarButton onClick={() => setState({ ...state, createOrderOpen: true })}>
                    New Order
                </ControlBarButton>
            </ControlBar>

            <OrderTable
                eventId={eventId}
                onOrderSelected={setSelectedOrder}
                dependency={state.refreshToken}
            />

            <FullScreenModal
                open={state.selectedOrder ? true : false}
                onClose={() => setSelectedOrder(null)}
                title="Order Details"
            >
                <OrderDetailModal
                    tranId={state.selectedOrder}
                    eventId={eventId}
                    onUpdate={handleOrderUpdate}
                />
            </FullScreenModal>

            <Comp
                open={state.createOrderOpen}
                onClose={() => setState({ ...state, createOrderOpen: false })}
                eventId={eventId}
                onOrderCreate={handleOrderCreate}
            />
        </OrderPage>
    );
}

// Order Table Component
// Fetch and add onClick listeners to all orders for an event
const OrderTable = memo(
    (props) => {
        // Props
        const { eventId, onOrderSelected, dependency } = props;

        // State and Hooks
        const [category, setCategory] = useState(0);

        const [data, loading, error] = useExpresso(
            `/apiv2/events/${eventId}/orders`,
            null,
            null,
            null,
            [eventId, dependency],
        );

        let orders = data?.orders || [];

        // ***** Filter List by Category ***** //

        if (category === 1) {
            orders = orders.filter((o) => o.status_financial >= 1000 && o.status_financial <= 1999);
        }

        if (category === 2) {
            orders = orders.filter((o) => o.status_financial === 3000);
        }

        if (category === 3) {
            orders = orders.filter((o) => o.status_financial >= 2000 && o.status_financial <= 2999);
        }

        if (category === 4) {
            orders = orders.filter(
                (o) => o.discount_code !== null && o.discount_code !== 'sys_discount',
            );
        }

        if (category === 5) {
            orders = orders.filter((o) => o.discount_code === 'sys_discount');
        }

        if (category === 6) {
            orders = orders.filter((o) => Number(o.comment_count) > 0);
        }

        if (loading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" height={'100%'}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" height={'100%'}>
                    <Typography variant="subtitle2">There was a problem loading orders</Typography>
                </Box>
            );
        }

        return (
            <Container>
                <Tabs
                    value={category}
                    onChange={(_, value) => setCategory(value)}
                    variant="scrollable"
                >
                    <Tab label={'All'} />
                    <Tab
                        label={
                            <Box display="flex" alignItems={'center'}>
                                <ApprovedTabIcon fontSize="small" />
                                <Typography variant="button">APPROVED</Typography>
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems={'center'}>
                                <RefundTabIcon fontSize="small" />
                                <Typography variant="button">REFUNDED</Typography>
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems={'center'}>
                                <DeclineTabIcon fontSize="small" />
                                <Typography variant="button">DECLINED</Typography>
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems={'center'}>
                                <DiscountTabIcon fontSize="small" />
                                <Typography variant="button">DISCOUNTED</Typography>
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems={'center'}>
                                <CompTabIcon fontSize="small" />
                                <Typography variant="button">COMPED</Typography>
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems={'center'}>
                                <CommentTabIcon fontSize="small" />
                                <Typography variant="button">COMMENTS</Typography>
                            </Box>
                        }
                    />
                </Tabs>

                <Box display="flex" flexDirection={'column'} flexGrow={1}>
                    <OrderDataGrid orders={orders} onOrderSelected={onOrderSelected} />
                </Box>
            </Container>
        );
        // Component won't re render if old and new props are the same
    },
    (oldProp, newProp) => {
        if (oldProp.eventId === newProp.eventId && oldProp.dependency === newProp.dependency) {
            return true;
        } else {
            return false;
        }
    },
);

function OrderDataGrid(props) {
    const { orders, onOrderSelected } = props;

    const [dataset, setDataset] = useState([]); // Filtered list of events
    const [searchText, setSearchText] = useState('');

    // Whenever the list of events from the parent component is updated, we need to update the dataset, and clear all filters to keep the UI in sync.
    // On first render, this will just set the initial value for 'dataset'
    useEffect(() => {
        setDataset(orders);
        setSearchText('');
    }, [orders]);

    // ***** Search / Filter ***** //

    const requestSearch = (searchValue) => {
        setSearchText(searchValue);

        const searchRegex = new RegExp(
            searchValue.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'),
            'i',
        );

        const filteredRows = orders.filter((row) => {
            const email = row.email.trim().toLowerCase();
            const orderNumber = 'F' + row.tran_id;
            const name =
                row.first_name.trim().toLowerCase() + ' ' + row.last_name.trim().toLowerCase();
            const phone = row.phone;

            return (
                searchRegex.test(email) || searchRegex.test(orderNumber) || searchRegex.test(name) || searchRegex.test(phone)
            );
        });

        setDataset(filteredRows);
    };

    // ***** DataGrid Rows and Columns ***** //

    const rows = dataset.map((o) => ({
        id: o.tran_id,
        c1: o.status_financial,
        c2: 'F' + o.tran_id,
        c3: { discountCode: o.discount_code, commentCount: o.comment_count, compedBy: o.comped_by },
        c4: o.total,
        c5: o.total_tickets,
        c6: o.email,
        c7: o.first_name + ' ' + o.last_name,
        c8: o.phone,
        c9: o.created_at,
    }));

    const columns = [
        {
            field: 'c1',
            headerName: ' ',
            width: 60,
            hideSortIcons: true,
            renderCell: (params) => <PaymentIcon status={params.value} />,
        },
        { field: 'c2', headerName: 'ORDER', width: 90, hideSortIcons: true },
        {
            field: 'c3',
            headerName: 'DETAIL',
            width: 70,
            hideSortIcons: true,
            renderCell: (params) => (
                <Box display={'flex'} alignItems={'center'}>
                    <DiscountIcon
                        code={params.value.discountCode}
                        compedBy={params.value.compedBy}
                    />
                    <CommentIcon count={params.value.commentCount} />
                </Box>
            ),
        },
        {
            field: 'c4',
            headerName: 'TOTAL',
            width: 100,
            hideSortIcons: true,
            type: 'number',
            valueFormatter: (params) => formatPrice(params.value),
        },
        { field: 'c5', headerName: 'TIX', width: 60, hideSortIcons: true, type: 'number' },
        { field: 'c6', headerName: 'EMAIL', minWidth: 260, flex: 1, hideSortIcons: true },
        { field: 'c7', headerName: 'NAME', minWidth: 200, flex: 1, hideSortIcons: true },
        { field: 'c8', headerName: 'PHONE', minWidth: 120, flex: 1, hideSortIcons: true },
        {
            field: 'c9',
            headerName: 'DATE',
            minWidth: 180,
            hideSortIcons: true,
            valueFormatter: (params) => {
                const value = moment(params.value);
                if (value.get('year') === moment().get('year')) {
                    return moment(params.value).format('MMM D, h:mma');
                } else {
                    return moment(params.value).format('MMM D YYYY h:mma');
                }
            },
        },
    ];

    return (
        <DG
            onRowClick={(params) => onOrderSelected(params.id)}
            disableSelectionOnClick
            components={{ Toolbar: Toolbar }}
            componentsProps={{
                toolbar: {
                    value: searchText,
                    onChange: (event) => requestSearch(event.target.value),
                    clearSearch: () => requestSearch(''),
                },
            }}
            rows={rows}
            columns={columns}
            disableColumnMenu
            sortingOrder={['desc', 'asc']}
        />
    );
}

function Toolbar(props) {
    const { value, onChange, clearSearch } = props;

    return (
        <SearchBar
            variant="outlined"
            size="small"
            value={value}
            onChange={onChange}
            placeholder="Search by name, email, order number"
            InputProps={{
                startAdornment: <SearchIcon fontSize="small" />,
                endAdornment: (
                    <IconButton
                        title="Clear"
                        aria-label="Clear"
                        size="small"
                        style={{ visibility: value ? 'visible' : 'hidden' }}
                        onClick={clearSearch}
                    >
                        <ClearIcon fontSize="small" />
                    </IconButton>
                ),
            }}
        />
    );
}

const DG = styled(DataGrid)(({ theme }) => ({
    '& .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '& .MuiDataGrid-dataContainer': {
        '&:hover': {
            cursor: 'pointer',
        },
    },
}));

const SearchBar = styled(TextField)(({ theme }) => ({
    margin: theme.spacing(2),
    marginBottom: theme.spacing(1),
    width: '360px',
}));

const Container = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
}));

const ApprovedTabIcon = styled(CheckCircleOutlinedIcon)(({ theme }) => ({
    marginRight: theme.spacing(0.5),
}));
const RefundTabIcon = styled(RotateLeftIcon)(({ theme }) => ({
    marginRight: theme.spacing(0.5),
}));
const DeclineTabIcon = styled(ErrorOutlineIcon)(({ theme }) => ({
    marginRight: theme.spacing(0.5),
}));
const DiscountTabIcon = styled(LocalOfferIcon)(({ theme }) => ({
    marginRight: theme.spacing(0.5),
}));
const CompTabIcon = styled(CardGiftcardIcon)(({ theme }) => ({
    marginRight: theme.spacing(0.5),
}));
const CommentTabIcon = styled(ModeCommentOutlinedIcon)(({ theme }) => ({
    marginRight: theme.spacing(0.5),
}));
